import React, { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { Form, Input, List } from 'antd-mobile-v5'
import { WhiteSpace } from 'antd-mobile'
import { FormattedMessage, useIntl } from 'react-intl'
import DetailStatus from '../../components/common/DetailStatus'
import { formatDate, languagesMapping, removeExpires } from '../../components/common/utils'
import { fdiTApproved } from '../../services/api'
import { usePayFdi } from '../../services/hooks'
import DiverInfo from '../../components/FormTools/DiverInfo'
import Header from '../../components/FormTools/FormHeader'
import UploadOssFile from '../../components/FormTools/UploadOssFile'
import Signature from '../../components/FormTools/Signature'
import SearchInstructor from '../../components/FormTools/SearchInstructor'
import SearchStoreInfo from '../../components/FormTools/SearchStoreInfo'
import DatePickerTool from '../../components/FormTools/DatePicker'
import PickerTool from '../../components/FormTools/Picker'
import AddressForm from '../../components/FormTools/AddressForm'
import dayjs from 'dayjs'

const Item = List.Item

const FdiTDetail = () => {
  const [form] = Form.useForm()
  const { formatMessage } = useIntl()
  const [signature, setSignature] = useState('')
  const [instructorVerify, setInstructorVerify] = useState(false)
  const [storeVerify, setStoreVerify] = useState(false)
  const { detail, ossSignature } = useSelector((state) => ({
    detail: state.FDI.FDITDetail,
    ossSignature: state.oss.ossSignature,
  }))
  const { runAsync: toPay } = usePayFdi()

  const editable = !!(detail.status === 'WAIT_CONFIRM' || detail.status === 'ADMIN_NOT_APPROVED')
  const filename = (type) => `${ossSignature.dir}${type}/${new Date().getTime()}`

  useEffect(() => {
    form.setFieldsValue({
      efr: detail.efr,
      mfd: detail.mfd,
      owsi: detail.owsi,
      fdifiles: detail.fdifiles || [],
      efrfiles: detail.efrfiles || [],
      otherFiles: detail.otherFiles || [],
      courseDate: new Date(detail.courseDate) || '',
      instructorNumber: detail.instructorInfoDTO ? detail.instructorInfoDTO.instructorNumber : '',
      storeNumber: detail.storeInfoDTO ? detail.storeInfoDTO.storeNumber : '',
      languageId: detail.languageId || '',
      regionId: detail.regionDTO ? detail.regionDTO.region : '',
      courseLocation: detail.courseLocation || '',
      addressRegionId: detail.addressRegionDTO ? detail.addressRegionDTO.id : '',
      addressStateId: detail.addressStateId || '',
      address: detail.address || '',
    })
    setSignature(detail.handwrittenSignatureUrlTrainer)
  }, [detail])

  const getSubmitData = () => {
    const values = form.getFieldsValue(true)
    const { storeNumber, regionId, courseDate, fdifiles, efrfiles, otherFiles, ...data } = values
    let newData = {
      ...data,
      courseDate: formatDate(courseDate, 'YYYY-MM-DD'),
      fdifiles: removeExpires(fdifiles),
      efrfiles: removeExpires(efrfiles),
      otherFiles: removeExpires(otherFiles),
    }
    if (storeVerify) newData.storeNumber = storeNumber
    return newData
  }

  if (!Object.keys(detail).length > 0) return null
  return (
    <>
      <Header text={<FormattedMessage id="fdi-app" />} code={detail?.code} />
      <DiverInfo info={detail.userInfoDTO} />
      <Form form={form} validateTrigger="onSubmit">
        <Form.Header>先决条件</Form.Header>
        {detail.efr ? (
          <Item>{`EFR ${formatMessage({ id: 'afdi-satisfy' })}`}</Item>
        ) : (
          <Form.Item
            name="efrfiles"
            label={'EFR'}
            form={form}
            rules={[
              {
                required: true,
                message: formatMessage({ id: 'form-upload-error' }),
              },
            ]}
          >
            <UploadOssFile
              form={form}
              fileName={filename('efr')}
              ossSignature={ossSignature}
              onSuccess={(file) => form.setFieldsValue({ efrfiles: file })}
              editable={editable}
              fileList={form.getFieldValue('efrfiles')}
              tips={<FormattedMessage id="common-upload-file-reminder" />}
            />
          </Form.Item>
        )}
        {detail.mfd && <Item>{formatMessage({ id: 'fdi-master-cert' }) + `（${detail.mfd}）`}</Item>}
        {detail.fdifiles && (
          <Form.Item
            name="fdifiles"
            label={formatMessage({ id: 'fdi-not-padi-cert' })}
            form={form}
            rules={[
              {
                required: true,
                message: formatMessage({ id: 'form-upload-error' }),
              },
            ]}
          >
            <UploadOssFile
              form={form}
              fileName={filename('fdi')}
              ossSignature={ossSignature}
              onSuccess={(file) => form.setFieldsValue({ fdifiles: file })}
              editable={editable}
              fileList={form.getFieldValue('fdifiles')}
              tips={<FormattedMessage id="common-upload-file-reminder" />}
            />
          </Form.Item>
        )}
        {detail.owsi && <Item>{formatMessage({ id: 'fdi-owsi-cert' }) + formatMessage({ id: 'afdi-satisfy' })}</Item>}
        {detail.otherFiles && (
          <Form.Item
            name="otherFiles"
            label={formatMessage({ id: 'mermaid-other-files' })}
            form={form}
            rules={[
              {
                required: true,
                message: formatMessage({ id: 'form-upload-error' }),
              },
            ]}
          >
            <UploadOssFile
              form={form}
              fileName={filename('other')}
              ossSignature={ossSignature}
              onSuccess={(file) => form.setFieldsValue({ otherFiles: file })}
              editable={editable}
              fileList={form.getFieldValue('otherFiles')}
              tips={<FormattedMessage id="app-health" />}
            />
          </Form.Item>
        )}
        <Form.Header>训练信息</Form.Header>
        {(editable || (!editable && detail.instructorInfoDTO)) && (
          <SearchInstructor
            idType={'trainer'}
            type={'NONE'}
            form={form}
            editable={editable}
            instructorVerify={instructorVerify}
            setInstructorVerify={setInstructorVerify}
            required={true}
            initInstructorInfo={detail.instructorInfoDTO}
          />
        )}
        {(editable || (!editable && detail.storeInfoDTO)) && (
          <SearchStoreInfo
            form={form}
            setStoreVerify={setStoreVerify}
            storeVerify={storeVerify}
            editable={editable}
            initStoreInfo={detail.storeInfoDTO}
          />
        )}
        <Form.Item
          form={form}
          name="courseLocation"
          label={formatMessage({ id: 'si-course-location-title' })}
          rules={[
            {
              required: true,
              message: formatMessage({ id: 'si-course-location' }),
            },
          ]}
        >
          <Input disabled={!editable} />
        </Form.Item>
        <DatePickerTool
          name="courseDate"
          placeholder={formatMessage({ id: 'efr-course-date-error' })}
          disabled={!editable}
          label={formatMessage({ id: 'common-course-date' })}
          rules={[
            {
              required: true,
              message: formatMessage({ id: 'efr-course-date-error' }),
            },
          ]}
          max={dayjs().toDate()}
        />
        <Form.Header>教学地址及语言</Form.Header>
        <Form.Item name="regionId" label={formatMessage({ id: 'dm-region' })} disabled={true}>
          <Input />
        </Form.Item>
        <PickerTool
          name={'languageId'}
          form={form}
          rules={[
            {
              required: true,
              message: '语言不能为空',
            },
          ]}
          placeholder={formatMessage({ id: 'dm-lang-error' })}
          data={languagesMapping}
          label={formatMessage({ id: 'dm-lang-title' })}
          disabled={!editable}
        />
        <Form.Header>地址信息</Form.Header>
        <AddressForm form={form} editable={editable} />
      </Form>
      <WhiteSpace />
      <Signature
        title={formatMessage({ id: 'dm-form-signature' })}
        filePath={'signature'}
        modified={editable}
        oss={ossSignature}
        onSuccess={(imgUrl) => setSignature(imgUrl)}
        onClear={() => setSignature('')}
        initSignature={detail.handwrittenSignatureUrlTrainer}
      />
      <WhiteSpace />
      <DetailStatus
        form={form}
        detail={detail}
        approveFn={fdiTApproved}
        getSubmitData={getSubmitData}
        payFn={toPay}
        listUrl={'/fdi_app_list'}
        payApplicationType={'MEMBER_APPLICATION_FDI'}
        signature={signature}
      />
    </>
  )
}

export default FdiTDetail
